import { useEffect, useMemo, useState } from "react";
import { Country, City } from "country-state-city";
import { ChevronDown, Search, MapPin, Check } from "lucide-react";
import { useLang } from "../context/LangContext";
import { localizedCityName, localizedCountryName } from "../lib/locationFormat";

/**
 * LocationPicker — searchable Country → City picker with live AR/EN localization.
 *
 * Props:
 *   country         (string, English canonical name – stored value)
 *   countryCode     (ISO-2)
 *   city            (string, English canonical name – stored value)
 *   onChange({country, countryCode, city})
 *   disabled        (read-only chips)
 *   testIdPrefix
 */
export default function LocationPicker({
    country = "",
    countryCode: initialCountryCode = "",
    city = "",
    onChange,
    disabled = false,
    testIdPrefix = "loc",
}) {
    const { t, lang } = useLang();

    // Resolve countryCode if only English name was provided.
    const [countryCode, setCountryCode] = useState(() => {
        if (initialCountryCode) return initialCountryCode;
        if (!country) return "";
        const found = Country.getAllCountries().find((c) => c.name === country);
        return found?.isoCode || "";
    });

    const [openCountry, setOpenCountry] = useState(false);
    const [openCity, setOpenCity] = useState(false);
    const [qCountry, setQCountry] = useState("");
    const [qCity, setQCity] = useState("");

    const allCountries = useMemo(() => Country.getAllCountries(), []);
    const cities = useMemo(
        () => (countryCode ? City.getCitiesOfCountry(countryCode) || [] : []),
        [countryCode]
    );

    // Build localized lists (memoised on lang change → instant switch).
    const localizedCountries = useMemo(() => {
        return allCountries
            .map((c) => ({
                isoCode: c.isoCode,
                en: c.name,
                label: localizedCountryName(c.isoCode, lang) || c.name,
            }))
            .sort((a, b) => a.label.localeCompare(b.label, lang === "ar" ? "ar" : "en"));
    }, [allCountries, lang]);

    const localizedCities = useMemo(() => {
        const seen = new Set();
        const out = [];
        for (const c of cities) {
            const label = localizedCityName(countryCode, c.name, lang) || c.name;
            // Dedupe by visible label so the same city in different governorates
            // doesn't appear twice.
            const key = label.toLowerCase();
            if (seen.has(key)) continue;
            seen.add(key);
            out.push({ en: c.name, label });
        }
        // Sort alphabetically in the active language so Arabic users get an
        // Arabic-collation order and English users a Latin one.
        out.sort((a, b) =>
            a.label.localeCompare(b.label, lang === "ar" ? "ar" : "en")
        );
        return out;
    }, [cities, countryCode, lang]);

    const filteredCountries = useMemo(() => {
        const q = qCountry.trim().toLowerCase();
        if (!q) return localizedCountries.slice(0, 250);
        return localizedCountries.filter(
            (c) =>
                c.label.toLowerCase().includes(q) ||
                c.en.toLowerCase().includes(q) ||
                c.isoCode.toLowerCase().includes(q)
        );
    }, [localizedCountries, qCountry]);

    const filteredCities = useMemo(() => {
        const q = qCity.trim().toLowerCase();
        if (!q) return localizedCities.slice(0, 300);
        return localizedCities
            .filter((c) => c.label.toLowerCase().includes(q) || c.en.toLowerCase().includes(q))
            .slice(0, 300);
    }, [localizedCities, qCity]);

    useEffect(() => {
        if (!openCountry && !openCity) return;
        const handler = (e) => {
            const root = e.target.closest(`[data-loc-root="${testIdPrefix}"]`);
            if (!root) {
                setOpenCountry(false);
                setOpenCity(false);
            }
        };
        document.addEventListener("mousedown", handler);
        return () => document.removeEventListener("mousedown", handler);
    }, [openCountry, openCity, testIdPrefix]);

    const pickCountry = (c) => {
        setCountryCode(c.isoCode);
        setOpenCountry(false);
        setQCountry("");
        onChange?.({ country: c.en, countryCode: c.isoCode, city: "" });
    };

    const pickCity = (cityObj) => {
        setOpenCity(false);
        setQCity("");
        onChange?.({ country, countryCode, city: cityObj.en });
    };

    // Display label for the currently selected country / city in the chosen language.
    const countryLabel = countryCode ? localizedCountryName(countryCode, lang) : "";
    const cityLabel = city ? localizedCityName(countryCode, city, lang) : "";

    if (disabled) {
        return (
            <div
                className="flex items-center gap-2 rounded-lg border border-emerald-200 bg-emerald-50/60 px-3 py-2 text-sm text-emerald-800"
                data-testid={`${testIdPrefix}-locked`}
            >
                <MapPin className="h-4 w-4 shrink-0" />
                <span className="font-semibold">{countryLabel || country}</span>
                <span className="text-emerald-600/70">—</span>
                <span className="font-semibold">{cityLabel || city}</span>
                <span className="ms-auto text-[11px] text-emerald-600/70">
                    {t.locationLocked}
                </span>
            </div>
        );
    }

    const searchIconSide = lang === "ar" ? { right: "1rem" } : { left: "1rem" };
    const searchInputPad = lang === "ar" ? "pe-9 ps-3" : "ps-9 pe-3";

    return (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2" data-loc-root={testIdPrefix}>
            {/* Country */}
            <div className="relative">
                <label className="mb-1.5 block text-sm font-medium">{t.countryLabel}</label>
                <button
                    type="button"
                    onClick={() => {
                        setOpenCountry((v) => !v);
                        setOpenCity(false);
                    }}
                    className="flex h-11 w-full items-center justify-between gap-2 rounded-md border border-neutral-200 bg-white px-3 text-sm text-neutral-800 hover:border-amber-400 focus:border-amber-500 focus:outline-none"
                    data-testid={`${testIdPrefix}-country-btn`}
                >
                    <span className={countryLabel ? "" : "text-neutral-400"}>
                        {countryLabel || t.countryPlaceholder}
                    </span>
                    <ChevronDown className="h-4 w-4 text-neutral-400" />
                </button>
                {openCountry ? (
                    <div className="absolute z-30 mt-1 max-h-72 w-full overflow-hidden rounded-md border border-neutral-200 bg-white shadow-lg">
                        <div className="relative border-b border-neutral-100 p-2">
                            <Search className="pointer-events-none absolute top-1/2 h-4 w-4 -translate-y-1/2 text-neutral-400" style={searchIconSide} />
                            <input
                                value={qCountry}
                                onChange={(e) => setQCountry(e.target.value)}
                                placeholder={t.searchCountry}
                                autoFocus
                                className={`h-9 w-full rounded-md border border-neutral-200 bg-neutral-50 text-sm outline-none focus:border-amber-400 ${searchInputPad}`}
                                data-testid={`${testIdPrefix}-country-search`}
                            />
                        </div>
                        <div className="max-h-56 overflow-y-auto subtle-scroll">
                            {filteredCountries.length === 0 ? (
                                <div className="px-3 py-3 text-sm text-neutral-400">—</div>
                            ) : (
                                filteredCountries.map((c) => (
                                    <button
                                        key={c.isoCode}
                                        type="button"
                                        onClick={() => pickCountry(c)}
                                        className={`flex w-full items-center justify-between gap-2 px-3 py-2 text-start text-sm hover:bg-amber-50 ${
                                            countryCode === c.isoCode ? "bg-amber-50/60" : ""
                                        }`}
                                        data-testid={`${testIdPrefix}-country-opt-${c.isoCode}`}
                                    >
                                        <span className="truncate">{c.label}</span>
                                        {countryCode === c.isoCode ? (
                                            <Check className="h-4 w-4 text-amber-600" />
                                        ) : null}
                                    </button>
                                ))
                            )}
                        </div>
                    </div>
                ) : null}
            </div>

            {/* City */}
            <div className="relative">
                <label className="mb-1.5 block text-sm font-medium">{t.cityLabel}</label>
                <button
                    type="button"
                    onClick={() => {
                        if (!countryCode) return;
                        setOpenCity((v) => !v);
                        setOpenCountry(false);
                    }}
                    disabled={!countryCode}
                    className="flex h-11 w-full items-center justify-between gap-2 rounded-md border border-neutral-200 bg-white px-3 text-sm text-neutral-800 hover:border-amber-400 focus:border-amber-500 focus:outline-none disabled:cursor-not-allowed disabled:bg-neutral-50 disabled:text-neutral-400"
                    data-testid={`${testIdPrefix}-city-btn`}
                >
                    <span className={cityLabel ? "" : "text-neutral-400"}>
                        {cityLabel || t.cityPlaceholder}
                    </span>
                    <ChevronDown className="h-4 w-4 text-neutral-400" />
                </button>
                {openCity ? (
                    <div className="absolute z-30 mt-1 max-h-72 w-full overflow-hidden rounded-md border border-neutral-200 bg-white shadow-lg">
                        <div className="relative border-b border-neutral-100 p-2">
                            <Search className="pointer-events-none absolute top-1/2 h-4 w-4 -translate-y-1/2 text-neutral-400" style={searchIconSide} />
                            <input
                                value={qCity}
                                onChange={(e) => setQCity(e.target.value)}
                                placeholder={t.searchCity}
                                autoFocus
                                className={`h-9 w-full rounded-md border border-neutral-200 bg-neutral-50 text-sm outline-none focus:border-amber-400 ${searchInputPad}`}
                                data-testid={`${testIdPrefix}-city-search`}
                            />
                        </div>
                        <div className="max-h-56 overflow-y-auto subtle-scroll">
                            {filteredCities.length === 0 ? (
                                <div className="px-3 py-3 text-sm text-neutral-400">—</div>
                            ) : (
                                filteredCities.map((c, idx) => (
                                    <button
                                        key={`${c.en}-${idx}`}
                                        type="button"
                                        onClick={() => pickCity(c)}
                                        className={`flex w-full items-center justify-between gap-2 px-3 py-2 text-start text-sm hover:bg-amber-50 ${
                                            city === c.en ? "bg-amber-50/60" : ""
                                        }`}
                                        data-testid={`${testIdPrefix}-city-opt-${idx}`}
                                    >
                                        <span className="truncate">{c.label}</span>
                                        {city === c.en ? (
                                            <Check className="h-4 w-4 text-amber-600" />
                                        ) : null}
                                    </button>
                                ))
                            )}
                        </div>
                    </div>
                ) : null}
            </div>
        </div>
    );
}
